import { Helmet } from "react-helmet-async";

interface JsonLdProps {
  data: Record<string, unknown> | Record<string, unknown>[];
}

export const JsonLd = ({ data }: JsonLdProps) => {
  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(data)}
      </script>
    </Helmet>
  );
};

// Organization schema for the company
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "HostCaptain",
  description: "The command center for Turo hosts. Playbooks, dashboards, and tools to run your Turo business like a pro.",
  url: "https://hostcaptain.co",
  logo: "https://hostcaptain.co/favicon.ico",
  sameAs: [],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    url: "https://hostcaptain.co",
  },
};

// WebSite schema for search functionality
export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "HostCaptain",
  url: "https://hostcaptain.co",
  description: "The command center for Turo hosts. Manage your fleet, track earnings, and scale your business.",
};

// SoftwareApplication schema for the product
export const softwareApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "HostCaptain",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description: "A comprehensive management platform for Turo car sharing hosts. Track earnings, manage expenses, analyze vehicle performance, and follow expert playbooks.",
  offers: [
    {
      "@type": "Offer",
      name: "Free Plan",
      price: "0",
      priceCurrency: "USD",
      description: "Perfect for new or part-time hosts with 1 car",
    },
    {
      "@type": "Offer",
      name: "Pro Plan",
      price: "29",
      priceCurrency: "USD",
      billingIncrement: "P1M",
      description: "For growing hosts with 2-5 cars",
    },
    {
      "@type": "Offer",
      name: "Scale Plan",
      price: "79",
      priceCurrency: "USD",
      billingIncrement: "P1M",
      description: "For scaling fleets with 6-15 cars",
    },
  ],
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5",
    ratingCount: "2",
    bestRating: "5",
    worstRating: "1",
  },
};

// FAQ schema for pricing page
export const pricingFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Can I cancel anytime?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes, you can cancel your subscription at any time. You'll continue to have access until the end of your billing period.",
      },
    },
    {
      "@type": "Question",
      name: "Is there a free trial?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The Free tier is available forever. For Pro and Scale, we offer a 14-day free trial so you can test all features.",
      },
    },
    {
      "@type": "Question",
      name: "Can I switch plans?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Absolutely. Upgrade or downgrade at any time.",
      },
    },
  ],
};

// Product schema with pricing offers
export const productWithOffersSchema = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "HostCaptain",
  description: "The command center for Turo hosts. Playbooks, dashboards, and tools to run your Turo business like a pro.",
  image: "https://hostcaptain.co/og-image.jpg",
  brand: {
    "@type": "Brand",
    name: "HostCaptain",
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5",
    ratingCount: "2",
    bestRating: "5",
    worstRating: "1",
  },
  review: [
    {
      "@type": "Review",
      author: {
        "@type": "Person",
        name: "Turo Host",
      },
      datePublished: "2025-12-01",
      reviewBody: "HostCaptain finally gave me clarity on which cars are actually making money. The playbooks saved me during my first damage claim.",
      reviewRating: {
        "@type": "Rating",
        ratingValue: "5",
        bestRating: "5",
      },
    },
  ],
  offers: [
    {
      "@type": "Offer",
      name: "Free",
      price: "0",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      priceValidUntil: "2027-12-31",
      url: "https://hostcaptain.co/pricing",
      itemCondition: "https://schema.org/NewCondition",
    },
    {
      "@type": "Offer",
      name: "Pro",
      price: "29",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      priceValidUntil: "2027-12-31",
      url: "https://hostcaptain.co/pricing",
      itemCondition: "https://schema.org/NewCondition",
    },
    {
      "@type": "Offer",
      name: "Scale",
      price: "79",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      priceValidUntil: "2027-12-31",
      url: "https://hostcaptain.co/pricing",
      itemCondition: "https://schema.org/NewCondition",
    },
  ],
};
